import { useMemo } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import { PageLayout } from '@/components/ui/PageLayout';
import { Tabs, type TabItem } from '@/components/ui/Tabs';
import { useReceptionDesk } from '@/hooks/useReceptionDesk';

type EconomyTabId = 'invoices' | 'queue' | 'payments' | 'quotes' | 'reports';

function economyTabFromPath(pathname: string): EconomyTabId {
  if (pathname.startsWith('/economy/queue')) return 'queue';
  if (pathname.startsWith('/economy/payments')) return 'payments';
  if (pathname.startsWith('/economy/quotes')) return 'quotes';
  if (pathname.startsWith('/economy/reports')) return 'reports';
  return 'invoices';
}

export function Economy() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const { activeClinicId } = useActiveClinic();

  const canInvoices = Boolean(activeClinicId && hasPermission(activeClinicId, 'invoices', 'view'));
  const canPayments = Boolean(activeClinicId && hasPermission(activeClinicId, 'payments', 'view'));
  const canQuotes = Boolean(activeClinicId && hasPermission(activeClinicId, 'quotes', 'view'));
  const canReports = Boolean(activeClinicId && hasPermission(activeClinicId, 'reports', 'view'));
  const desk = useReceptionDesk(canInvoices ? activeClinicId : null);
  const queueCount = desk.data?.queue.length;

  const items = useMemo<TabItem[]>(() => {
    const tabs: TabItem[] = [];
    if (canInvoices) tabs.push({ id: 'invoices', label: t('economy.tabs.invoices') });
    if (canInvoices) {
      tabs.push({
        id: 'queue',
        label: t('economy.tabs.queue'),
        badge: queueCount && queueCount > 0 ? queueCount : undefined,
      });
    }
    if (canPayments) tabs.push({ id: 'payments', label: t('economy.tabs.clinio_pay') });
    if (canQuotes) tabs.push({ id: 'quotes', label: t('economy.tabs.quotes') });
    if (canReports) tabs.push({ id: 'reports', label: t('economy.tabs.reports') });
    return tabs;
  }, [canInvoices, canPayments, canQuotes, canReports, queueCount, t]);

  const activeTab = economyTabFromPath(location.pathname);

  return (
    <PageLayout title={t('economy.title')}>
      {items.length > 0 ? (
        <Tabs
          items={items}
          value={items.some((item) => item.id === activeTab) ? activeTab : items[0].id}
          onChange={(id) => navigate(`/economy/${id}`)}
          overflowLabel={(count) => t('design.more_tabs', { count })}
          ariaLabel={t('economy.tabs_label')}
        />
      ) : null}

      <div className="mt-s5">
        <Outlet />
      </div>
    </PageLayout>
  );
}
